import React, { useState, useEffect, useRef, useCallback } from 'react';
import GameRanking from '../components/GameRanking'
import './KillerDeveloper.css';

const TILE_SIZE = 32;
const MAP_WIDTH = 25;
const MAP_HEIGHT = 18;
const CANVAS_WIDTH = MAP_WIDTH * TILE_SIZE;
const CANVAS_HEIGHT = MAP_HEIGHT * TILE_SIZE;

const VISION_RANGE = 150;
const GUARD_SPEED = 0.8; // Velocidade tranquila
const GUARD_ROTATION_SPEED = 0.002; // Muito lento
const PLAYER_SPEED = 4;
const BULLET_SPEED = 6;
const MOVE_COOLDOWN = 200; // ms entre movimentos

// Tiles
const TILE_FLOOR = 0;
const TILE_WALL = 1;
const TILE_DOOR = 2;
const TILE_EXIT = 3;
const TILE_OBSTACLE = 4; // Novo: obstáculos para se esconder

const generateHouseMap = (level) => {
  const map = Array(MAP_HEIGHT).fill(null).map(() => Array(MAP_WIDTH).fill(TILE_WALL));
  
  // Criar salas retangulares
  const rooms = [];
  const roomCount = 4 + Math.floor(level / 2);
  
  for (let i = 0; i < roomCount; i++) {
    const width = 4 + Math.floor(Math.random() * 5);
    const height = 4 + Math.floor(Math.random() * 5);
    const x = 2 + Math.floor(Math.random() * (MAP_WIDTH - width - 4));
    const y = 2 + Math.floor(Math.random() * (MAP_HEIGHT - height - 4));
    
    rooms.push({ x, y, width, height, centerX: x + Math.floor(width / 2), centerY: y + Math.floor(height / 2) });
    
    // Criar sala
    for (let ry = y; ry < y + height; ry++) {
      for (let rx = x; rx < x + width; rx++) {
        if (ry >= 0 && ry < MAP_HEIGHT && rx >= 0 && rx < MAP_WIDTH) {
          map[ry][rx] = TILE_FLOOR;
        }
      }
    }
  }
  
  // Conectar salas com corredores
  for (let i = 0; i < rooms.length - 1; i++) {
    const roomA = rooms[i];
    const roomB = rooms[i + 1];
    
    // Corredor horizontal
    const startX = Math.min(roomA.centerX, roomB.centerX);
    const endX = Math.max(roomA.centerX, roomB.centerX);
    for (let x = startX; x <= endX; x++) {
      if (x >= 0 && x < MAP_WIDTH && roomA.centerY >= 0 && roomA.centerY < MAP_HEIGHT) {
        map[roomA.centerY][x] = TILE_FLOOR;
      }
    }
    
    // Corredor vertical
    const startY = Math.min(roomA.centerY, roomB.centerY);
    const endY = Math.max(roomA.centerY, roomB.centerY);
    for (let y = startY; y <= endY; y++) {
      if (y >= 0 && y < MAP_HEIGHT && roomB.centerX >= 0 && roomB.centerX < MAP_WIDTH) {
        map[y][roomB.centerX] = TILE_FLOOR;
      }
    }
  }
  
  // Adicionar portas
  for (let y = 1; y < MAP_HEIGHT - 1; y++) {
    for (let x = 1; x < MAP_WIDTH - 1; x++) {
      if (map[y][x] === TILE_FLOOR) {
        const walls = [
          map[y - 1][x] === TILE_WALL,
          map[y + 1][x] === TILE_WALL,
          map[y][x - 1] === TILE_WALL,
          map[y][x + 1] === TILE_WALL
        ];
        const wallCount = walls.filter(Boolean).length;
        if (wallCount === 2 && Math.random() > 0.85) {
          map[y][x] = TILE_DOOR;
        }
      }
    }
  }
  
  // Saída na última sala
  const exitRoom = rooms[rooms.length - 1];
  map[exitRoom.centerY][exitRoom.centerX] = TILE_EXIT;
  
  // PASSO 7: Adicionar obstáculos nas salas (para se esconder)
  for (let room of rooms) {
    // 2-3 obstáculos por sala
    const obstacleCount = 2 + Math.floor(Math.random() * 2);
    
    for (let i = 0; i < obstacleCount; i++) {
      const ox = room.x + 2 + Math.floor(Math.random() * (room.width - 4));
      const oy = room.y + 2 + Math.floor(Math.random() * (room.height - 4));
      
      // Verifica se não sobrepõe com centro da sala
      const distFromCenter = Math.abs(ox - room.centerX) + Math.abs(oy - room.centerY);
      
      if (distFromCenter > 2 && ox >= 0 && ox < MAP_WIDTH && oy >= 0 && oy < MAP_HEIGHT) {
        if (map[oy][ox] === TILE_FLOOR) {
          map[oy][ox] = TILE_OBSTACLE;
        }
      }
    }
  }
  
  return { map, rooms };
};

const KillerDeveloper = ({ user, onBackToMenu }) => {
  const canvasRef = useRef(null);
  const [gameState, setGameState] = useState('menu'); // menu, playing, paused, missionComplete, gameOver
  const [level, setLevel] = useState(1);
  const [score, setScore] = useState(0);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [missionTime, setMissionTime] = useState(0);
  
  const gameRef = useRef({
    map: [],
    rooms: [],
    player: { 
      x: 0, y: 0, 
      targetX: 0, targetY: 0,
      angle: 0, 
      health: 100,
      moving: false
    },
    guards: [],
    target: null,
    bullets: [],
    keys: {},
    targetKilled: false,
    playerSpotted: false,
    lastFrameTime: 0,
    lastMoveTime: 0,
    touchMoving: false,
    touchDirection: { x: 0, y: 0 }
  });

  const generateLevel = useCallback((lvl) => {
    const { map, rooms } = generateHouseMap(lvl);
    
    // Posição inicial do jogador (primeira sala)
    const startRoom = rooms[0];
    const player = {
      x: startRoom.centerX * TILE_SIZE + TILE_SIZE / 2,
      y: startRoom.centerY * TILE_SIZE + TILE_SIZE / 2,
      targetX: startRoom.centerX * TILE_SIZE + TILE_SIZE / 2,
      targetY: startRoom.centerY * TILE_SIZE + TILE_SIZE / 2,
      angle: 0,
      health: 100,
      moving: false
    };
    
    // Gerar guardas com patrulha
    const guards = [];
    const guardCount = 2 + Math.floor(lvl / 2);
    for (let i = 0; i < guardCount; i++) {
      const room = rooms[1 + Math.floor(Math.random() * (rooms.length - 1))];
      
      // Criar caminho de patrulha (4 pontos aleatórios na sala)
      const patrolPath = [];
      for (let j = 0; j < 4; j++) {
        const px = (room.x + 1 + Math.floor(Math.random() * (room.width - 2))) * TILE_SIZE + TILE_SIZE / 2;
        const py = (room.y + 1 + Math.floor(Math.random() * (room.height - 2))) * TILE_SIZE + TILE_SIZE / 2;
        patrolPath.push({ x: px, y: py });
      }
      
      guards.push({
        x: room.centerX * TILE_SIZE + TILE_SIZE / 2,
        y: room.centerY * TILE_SIZE + TILE_SIZE / 2,
        angle: Math.random() * Math.PI * 2,
        visionAngle: Math.PI / 3,
        patrolPath: patrolPath,
        currentPatrolIndex: 0,
        waitTime: 0,
        waitDuration: 60, // Frames para esperar em cada ponto
        state: 'patrol',
        health: 100
      });
    }
    
    // Gerar alvo (última sala)
    const targetRoom = rooms[rooms.length - 2] || rooms[rooms.length - 1];
    const target = {
      x: targetRoom.centerX * TILE_SIZE + TILE_SIZE / 2,
      y: targetRoom.centerY * TILE_SIZE + TILE_SIZE / 2,
      alive: true
    };
    
    gameRef.current = {
      map,
      rooms,
      player,
      guards,
      target,
      bullets: [],
      keys: {},
      targetKilled: false,
      playerSpotted: false,
      lastFrameTime: 0,
      lastMoveTime: 0,
      touchMoving: false,
      touchDirection: { x: 0, y: 0 }
    };
    
    setTimeElapsed(0);
    setMissionTime(0);
  }, []);

  const isWalkable = useCallback((x, y) => {
    const tileX = Math.floor(x / TILE_SIZE);
    const tileY = Math.floor(y / TILE_SIZE);
    
    if (tileX < 0 || tileX >= MAP_WIDTH || tileY < 0 || tileY >= MAP_HEIGHT) {
      return false;
    }
    
    const tile = gameRef.current.map[tileY][tileX];
    return tile !== TILE_WALL && tile !== TILE_OBSTACLE;
  }, []);

  const checkLineOfSight = useCallback((x1, y1, x2, y2) => {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const steps = Math.floor(distance / 4);
    
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const x = x1 + dx * t;
      const y = y1 + dy * t;
      
      const tileX = Math.floor(x / TILE_SIZE);
      const tileY = Math.floor(y / TILE_SIZE);
      
      if (tileX < 0 || tileX >= MAP_WIDTH || tileY < 0 || tileY >= MAP_HEIGHT) {
        return false;
      }
      
      if (gameRef.current.map[tileY][tileX] === TILE_WALL) {
        return false;
      }
      
      if (gameRef.current.map[tileY][tileX] === TILE_OBSTACLE) {
        return false;
      }
    }
    
    return true;
  }, []);

  const canGuardSeePlayer = useCallback((guard) => {
    const dx = gameRef.current.player.x - guard.x;
    const dy = gameRef.current.player.y - guard.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    if (distance > VISION_RANGE) return false;
    
    const angleToPlayer = Math.atan2(dy, dx);
    let angleDiff = angleToPlayer - guard.angle;
    
    // Normalizar ângulo
    while (angleDiff > Math.PI) angleDiff -= Math.PI * 2;
    while (angleDiff < -Math.PI) angleDiff += Math.PI * 2;
    
    if (Math.abs(angleDiff) > guard.visionAngle / 2) return false;
    
    return checkLineOfSight(guard.x, guard.y, gameRef.current.player.x, gameRef.current.player.y);
  }, [checkLineOfSight]);

  const updateGame = useCallback((timestamp) => {
    if (gameState !== 'playing') return;
    
    const game = gameRef.current;
    const currentTime = timestamp || Date.now();
    
    // Sistema de movimento tile-por-tile
    if (!game.player.moving) {
      // Pode receber novo comando
      let moveX = 0;
      let moveY = 0;
      
      // Input de teclado
      if (game.keys['w'] || game.keys['ArrowUp']) moveY = -1;
      else if (game.keys['s'] || game.keys['ArrowDown']) moveY = 1;
      
      if (game.keys['a'] || game.keys['ArrowLeft']) moveX = -1;
      else if (game.keys['d'] || game.keys['ArrowRight']) moveX = 1;
      
      // Input de touch (um movimento por toque)
      if (game.touchMoving && (currentTime - game.lastMoveTime > MOVE_COOLDOWN)) {
        moveX = game.touchDirection.x;
        moveY = game.touchDirection.y;
        game.touchMoving = false; // Reseta após processar
      }
      
      // Iniciar movimento se houver input
      if ((moveX !== 0 || moveY !== 0) && (currentTime - game.lastMoveTime > MOVE_COOLDOWN)) {
        // Calcula posição alvo (1 tile = 32px)
        const targetX = game.player.x + (moveX * TILE_SIZE);
        const targetY = game.player.y + (moveY * TILE_SIZE);
        
        // Verifica se pode mover
        if (isWalkable(targetX, targetY)) {
          game.player.targetX = targetX;
          game.player.targetY = targetY;
          game.player.moving = true;
          game.player.angle = Math.atan2(moveY, moveX);
          game.lastMoveTime = currentTime;
        }
      }
    } else {
      // Interpola movimento até o tile alvo
      const dx = game.player.targetX - game.player.x;
      const dy = game.player.targetY - game.player.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      if (distance < 2) {
        // Chegou no tile alvo
        game.player.x = game.player.targetX;
        game.player.y = game.player.targetY;
        game.player.moving = false;
      } else {
        // Move em direção ao alvo
        game.player.x += (dx / distance) * PLAYER_SPEED;
        game.player.y += (dy / distance) * PLAYER_SPEED;
      }
    }
    
    // Atualizar balas
    game.bullets = game.bullets.filter(bullet => {
      bullet.x += Math.cos(bullet.angle) * BULLET_SPEED;
      bullet.y += Math.sin(bullet.angle) * BULLET_SPEED;
      
      if (!isWalkable(bullet.x, bullet.y)) return false;
      
      // Checar colisão com guardas - SE ATIRAR EM GUARDA, PERDE!
      for (let guard of game.guards) {
        const dx = bullet.x - guard.x;
        const dy = bullet.y - guard.y;
        if (Math.sqrt(dx * dx + dy * dy) < 16) {
          // Atirou em guarda = GAME OVER
          game.playerSpotted = true;
          setGameState('gameOver');
          return false;
        }
      }
      
      // Checar colisão com alvo (ÚNICO PERMITIDO)
      if (game.target.alive) {
        const dx = bullet.x - game.target.x;
        const dy = bullet.y - game.target.y;
        if (Math.sqrt(dx * dx + dy * dy) < 16) {
          game.target.alive = false;
          game.targetKilled = true;
          setScore(s => s + 1000);
          return false;
        }
      }
      
      return true;
    });
    
    // Remover guardas mortos (não usado mais, mas mantém estrutura)
    game.guards = game.guards.filter(g => g.health > 0);
    
    // Atualizar guardas (velocidade ABSOLUTAMENTE CONSTANTE)
    game.guards.forEach(guard => {
      if (guard.state === 'patrol') {
        // Pegar ponto de patrulha atual
        const target = guard.patrolPath[guard.currentPatrolIndex];
        
        const dx = target.x - guard.x;
        const dy = target.y - guard.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance > 5) {
          // VELOCIDADE FIXA: Sempre 0.8 px/frame
          const dirX = dx / distance;
          const dirY = dy / distance;
          
          guard.x += dirX * 0.8; // HARD-CODED para garantir constância
          guard.y += dirY * 0.8; // HARD-CODED para garantir constância
          
          // Ângulo aponta para direção do movimento (sem animação)
          guard.angle = Math.atan2(dy, dx);
        } else {
          // Chegou no ponto, espera um pouco
          guard.waitTime++;
          
          // Rotaciona muito devagar enquanto espera (HARD-CODED)
          guard.angle += 0.002;
          
          if (guard.waitTime >= guard.waitDuration) {
            // Vai para próximo ponto
            guard.waitTime = 0;
            guard.currentPatrolIndex = (guard.currentPatrolIndex + 1) % guard.patrolPath.length;
          }
        }
      }
      
      // Verificar se vê o jogador
      if (canGuardSeePlayer(guard)) {
        game.playerSpotted = true;
        setGameState('gameOver');
      }
    });
    
    // Verificar saída
    if (game.targetKilled) {
      const exitTileX = Math.floor(game.player.x / TILE_SIZE);
      const exitTileY = Math.floor(game.player.y / TILE_SIZE);
      
      if (game.map[exitTileY][exitTileX] === TILE_EXIT) {
        const timeBonus = Math.max(0, 10000 - missionTime * 10);
        setScore(s => s + timeBonus + (level * 500));
        setGameState('missionComplete');
      }
    }
  }, [gameState, isWalkable, canGuardSeePlayer, level, missionTime]);

  const render = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const game = gameRef.current;
    
    // Fundo escuro
    ctx.fillStyle = '#0a0a0a';
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    
    // Desenhar mapa
    for (let y = 0; y < MAP_HEIGHT; y++) {
      for (let x = 0; x < MAP_WIDTH; x++) {
        const tile = game.map[y][x];
        
        if (tile === TILE_FLOOR) {
          ctx.fillStyle = '#1a1a1a';
          ctx.fillRect(x * TILE_SIZE, y * TILE_SIZE, TILE_SIZE, TILE_SIZE);
        } else if (tile === TILE_WALL) {
          ctx.fillStyle = '#2a2a2a';
          ctx.fillRect(x * TILE_SIZE, y * TILE_SIZE, TILE_SIZE, TILE_SIZE);
          ctx.strokeStyle = '#333';
          ctx.strokeRect(x * TILE_SIZE, y * TILE_SIZE, TILE_SIZE, TILE_SIZE);
        } else if (tile === TILE_DOOR) {
          ctx.fillStyle = '#3a2a1a';
          ctx.fillRect(x * TILE_SIZE, y * TILE_SIZE, TILE_SIZE, TILE_SIZE);
        } else if (tile === TILE_EXIT) {
          ctx.fillStyle = game.targetKilled ? '#00ff00' : '#004400';
          ctx.fillRect(x * TILE_SIZE, y * TILE_SIZE, TILE_SIZE, TILE_SIZE);
          ctx.font = '20px monospace';
          ctx.fillStyle = '#fff';
          ctx.textAlign = 'center';
          ctx.fillText('🚪', x * TILE_SIZE + TILE_SIZE / 2, y * TILE_SIZE + TILE_SIZE / 2 + 7);
        } else if (tile === TILE_OBSTACLE) {
          // Obstáculo (caixa/móvel para se esconder)
          ctx.fillStyle = '#4a3a2a';
          ctx.fillRect(x * TILE_SIZE, y * TILE_SIZE, TILE_SIZE, TILE_SIZE);
          ctx.strokeStyle = '#5a4a3a';
          ctx.lineWidth = 2;
          ctx.strokeRect(x * TILE_SIZE, y * TILE_SIZE, TILE_SIZE, TILE_SIZE);
          
          // Desenha um X para dar aparência de caixa
          ctx.strokeStyle = '#6a5a4a';
          ctx.beginPath();
          ctx.moveTo(x * TILE_SIZE + 4, y * TILE_SIZE + 4);
          ctx.lineTo(x * TILE_SIZE + TILE_SIZE - 4, y * TILE_SIZE + TILE_SIZE - 4);
          ctx.moveTo(x * TILE_SIZE + TILE_SIZE - 4, y * TILE_SIZE + 4);
          ctx.lineTo(x * TILE_SIZE + 4, y * TILE_SIZE + TILE_SIZE - 4);
          ctx.stroke();
        }
      }
    }
    
    // Desenhar alvo
    if (game.target.alive) {
      ctx.fillStyle = '#ff0000';
      ctx.shadowBlur = 20;
      ctx.shadowColor = '#ff0000';
      ctx.beginPath();
      ctx.arc(game.target.x, game.target.y, 12, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;
      
      ctx.font = '16px monospace';
      ctx.fillStyle = '#fff';
      ctx.textAlign = 'center';
      ctx.fillText('🎯', game.target.x, game.target.y + 5);
    }
    
    // Desenhar guardas com lanternas
    game.guards.forEach(guard => {
      // Cone de visão (lanterna)
      ctx.save();
      ctx.translate(guard.x, guard.y);
      ctx.rotate(guard.angle);
      
      const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, VISION_RANGE);
      gradient.addColorStop(0, 'rgba(255, 255, 100, 0.3)');
      gradient.addColorStop(0.5, 'rgba(255, 255, 100, 0.15)');
      gradient.addColorStop(1, 'rgba(255, 255, 100, 0)');
      
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.arc(0, 0, VISION_RANGE, -guard.visionAngle / 2, guard.visionAngle / 2);
      ctx.closePath();
      ctx.fill();
      
      ctx.restore();
      
      // Corpo do guarda
      ctx.fillStyle = '#ff4444';
      ctx.shadowBlur = 10;
      ctx.shadowColor = '#ff4444';
      ctx.beginPath();
      ctx.arc(guard.x, guard.y, 10, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;
      
      ctx.font = '14px monospace';
      ctx.fillStyle = '#fff';
      ctx.textAlign = 'center';
      ctx.fillText('👮', guard.x, guard.y + 5);
    });
    
    // Desenhar balas
    game.bullets.forEach(bullet => {
      ctx.fillStyle = '#ffff00';
      ctx.shadowBlur = 10;
      ctx.shadowColor = '#ffff00';
      ctx.beginPath();
      ctx.arc(bullet.x, bullet.y, 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;
    });
    
    // Desenhar jogador com arma
    ctx.save();
    ctx.translate(game.player.x, game.player.y);
    
    // Arma (apontando na direção do mouse)
    ctx.rotate(game.player.angle);
    ctx.fillStyle = '#666666';
    ctx.fillRect(8, -2, 12, 4); // Cano da arma
    ctx.fillStyle = '#444444';
    ctx.fillRect(6, -3, 4, 6); // Cabo da arma
    
    ctx.restore();
    
    // Corpo do jogador
    ctx.fillStyle = '#00ff00';
    ctx.shadowBlur = 15;
    ctx.shadowColor = '#00ff00';
    ctx.beginPath();
    ctx.arc(game.player.x, game.player.y, 10, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;
    
    ctx.font = '14px monospace';
    ctx.fillStyle = '#fff';
    ctx.textAlign = 'center';
    ctx.fillText('💻', game.player.x, game.player.y + 5);
    
    // HUD
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.fillRect(0, 0, CANVAS_WIDTH, 50);
    
    ctx.font = '16px monospace';
    ctx.fillStyle = '#00ff00';
    ctx.textAlign = 'left';
    ctx.fillText(`NÍVEL: ${level}`, 10, 25);
    ctx.fillText(`SCORE: ${score}`, 10, 42);
    
    ctx.textAlign = 'center';
    ctx.fillText(`TEMPO: ${Math.floor(missionTime / 60)}:${(missionTime % 60).toString().padStart(2, '0')}`, CANVAS_WIDTH / 2, 25);
    
    if (!game.targetKilled) {
      ctx.fillStyle = '#ff0000';
      ctx.fillText('🎯 ELIMINE O ALVO', CANVAS_WIDTH / 2, 42);
    } else {
      ctx.fillStyle = '#00ff00';
      ctx.fillText('✓ ALVO ELIMINADO - FUJA!', CANVAS_WIDTH / 2, 42);
    }
    
    ctx.textAlign = 'right';
    ctx.fillStyle = '#00ff00';
    ctx.fillText(`GUARDAS: ${game.guards.length}`, CANVAS_WIDTH - 10, 25);
    
  }, [level, score, missionTime]);

  const gameLoop = useCallback((timestamp) => {
    updateGame(timestamp);
    render();
    
    if (gameState === 'playing') {
      requestAnimationFrame(gameLoop);
    }
  }, [updateGame, render, gameState]);

  const togglePause = useCallback(() => {
    if (gameState === 'playing') {
      setGameState('paused');
    } else if (gameState === 'paused') {
      setGameState('playing');
    }
  }, [gameState]);

  useEffect(() => {
    if (gameState === 'playing') {
      const timer = setInterval(() => {
        setMissionTime(t => t + 1);
      }, 1000);
      
      requestAnimationFrame(gameLoop);
      
      return () => clearInterval(timer);
    }
  }, [gameState, gameLoop]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Pause com P ou Escape
      if (e.key === 'p' || e.key === 'P' || e.key === 'Escape') {
        e.preventDefault();
        togglePause();
        return;
      }
      
      if (gameState !== 'playing') return;
      
      gameRef.current.keys[e.key] = true;
      
      // Atirar com espaço
      if (e.key === ' ') {
        e.preventDefault();
        const game = gameRef.current;
        game.bullets.push({
          x: game.player.x,
          y: game.player.y,
          angle: game.player.angle
        });
      }
    };
    
    const handleKeyUp = (e) => {
      gameRef.current.keys[e.key] = false;
    };
    
    const handleMouseMove = (e) => {
      if (gameState !== 'playing') return;
      const canvas = canvasRef.current;
      if (!canvas) return;
      
      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      
      const dx = mouseX - gameRef.current.player.x;
      const dy = mouseY - gameRef.current.player.y;
      gameRef.current.player.angle = Math.atan2(dy, dx);
    };
    
    const handleClick = (e) => {
      if (gameState !== 'playing') return;
      e.preventDefault();
      
      const game = gameRef.current;
      game.bullets.push({
        x: game.player.x,
        y: game.player.y,
        angle: game.player.angle
      });
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.addEventListener('mousemove', handleMouseMove);
      canvas.addEventListener('click', handleClick);
    }
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      if (canvas) {
        canvas.removeEventListener('mousemove', handleMouseMove);
        canvas.removeEventListener('click', handleClick);
      }
    };
  }, [gameState, togglePause]);

  // Controles touch para mobile (tile-por-tile)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let touchStartX = 0;
    let touchStartY = 0;
    let touchStartTime = 0;

    const handleTouchStart = (e) => {
      if (gameState !== 'playing') return;
      const touch = e.touches[0];
      const rect = canvas.getBoundingClientRect();
      touchStartX = touch.clientX - rect.left;
      touchStartY = touch.clientY - rect.top;
      touchStartTime = Date.now();
    };

    const handleTouchEnd = (e) => {
      if (gameState !== 'playing') return;
      
      const touch = e.changedTouches[0];
      const rect = canvas.getBoundingClientRect();
      const touchEndX = touch.clientX - rect.left;
      const touchEndY = touch.clientY - rect.top;
      
      const dx = touchEndX - touchStartX;
      const dy = touchEndY - touchStartY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      // Swipe mínimo de 20px
      if (distance > 20) {
        const absDx = Math.abs(dx);
        const absDy = Math.abs(dy);
        
        // Determina direção (um tile por swipe)
        if (absDx > absDy) {
          // Movimento horizontal
          gameRef.current.touchDirection = { x: dx > 0 ? 1 : -1, y: 0 };
        } else {
          // Movimento vertical
          gameRef.current.touchDirection = { x: 0, y: dy > 0 ? 1 : -1 };
        }
        
        gameRef.current.touchMoving = true;
      }
    };

    canvas.addEventListener('touchstart', handleTouchStart);
    canvas.addEventListener('touchend', handleTouchEnd);

    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchend', handleTouchEnd);
    };
  }, [gameState]);

  const startGame = () => {
    generateLevel(level);
    setGameState('playing');
  };

  const nextLevel = () => {
    setLevel(l => l + 1);
    generateLevel(level + 1);
    setGameState('playing');
  };

  const restartGame = () => {
    setLevel(1);
    setScore(0);
    generateLevel(1);
    setGameState('playing');
  };

  if (gameState === 'menu') {
    return (
      <div className="killer-developer-container">
        <div className="killer-developer-menu">
          <h1 className="game-title">
            <span className="title-icon">💻</span>KILLER DEVELOPER<span className="title-icon">🔫</span>
          </h1>
          <p className="game-subtitle">Operação Stealth 16-bit</p>
          
          <div className="game-instructions">
            <h3>🎯 MISSÃO</h3>
            <p>• Elimine o alvo vermelho 🎯</p>
            <p>• Escape pela porta verde 🚪</p>
            <p>• Não seja visto pelos guardas 👮</p>
            
            <h3>🎮 CONTROLES</h3>
            <p>• WASD / Setas - Mover (1 tile por tecla)</p>
            <p>• Mouse - Mirar</p>
            <p>• Click / Espaço - Atirar</p>
            <p>• Swipe (Mobile) - Move 1 tile por swipe</p>
            <p>• P / ESC - Pausar</p>
            
            <h3>⚠️ REGRAS</h3>
            <p>• Fique fora do cone de luz amarelo</p>
            <p>• Atire APENAS no alvo vermelho 🎯</p>
            <p>• NÃO atire nos guardas (você perde!)</p>
            <p>• Pontuação por velocidade e eliminações</p>
          </div>
          
          <button className="btn-start" onClick={startGame}>
            INICIAR MISSÃO
          </button>
          
          {onBackToMenu && (
            <button className="btn-back" onClick={onBackToMenu}>
              ← Voltar ao Menu
            </button>
          )}
        </div>
      </div>
    );
  }

  if (gameState === 'missionComplete') {
    return (
      <div className="killer-developer-container">
        <div className="killer-developer-menu">
          <h1 className="mission-success">✓ MISSÃO COMPLETA</h1>
          <p className="mission-stats">Nível {level} concluído!</p>
          <p className="mission-stats">Tempo: {Math.floor(missionTime / 60)}:{(missionTime % 60).toString().padStart(2, '0')}</p>
          <p className="mission-score">SCORE TOTAL: {score}</p>
          
          <button className="btn-start" onClick={nextLevel}>
            PRÓXIMA MISSÃO →
          </button>
          
          <button className="btn-back" onClick={() => setGameState('menu')}>
            Menu Principal
          </button>
        </div>
      </div>
    );
  }

  if (gameState === 'gameOver') {
    return (
      <div className="killer-developer-container">
        <div className="killer-developer-menu">
          <h1 className="mission-failed">✗ MISSÃO FALHOU</h1>
          <p className="mission-stats">Você foi detectado!</p>
          <p className="mission-stats">Nível alcançado: {level}</p>
          <p className="mission-score">SCORE FINAL: {score}</p>
          
          <button className="btn-start" onClick={restartGame}>
            TENTAR NOVAMENTE
          </button>
          
          <button className="btn-back" onClick={() => setGameState('menu')}>
            Menu Principal
          </button>
        </div>
      </div>
    );
  }

  if (gameState === 'paused') {
    return (
      <div className="killer-developer-container">
        <canvas
          ref={canvasRef}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          className="killer-developer-canvas paused"
        />
        <div className="pause-overlay">
          <div className="pause-menu">
            <h2>⏸️ JOGO PAUSADO</h2>
            <button className="btn-start" onClick={togglePause}>
              ▶ CONTINUAR
            </button>
            <button className="btn-back" onClick={() => setGameState('menu')}>
              🏠 MENU PRINCIPAL
            </button>
            {onBackToMenu && (
              <button className="btn-back" onClick={onBackToMenu}>
                ← VOLTAR AO PORTAL
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="killer-developer-container">
      <canvas
        ref={canvasRef}
        width={CANVAS_WIDTH}
        height={CANVAS_HEIGHT}
        className="killer-developer-canvas"
      />
      
      {/* Botões de controle */}
      <div className="game-controls">
        <button className="control-btn pause-btn" onClick={togglePause}>
          ⏸️
        </button>
        <button className="control-btn exit-btn" onClick={() => setGameState('menu')}>
          🏠
        </button>
      </div>
      
      {/* Controles Mobile Touch para Atirar */}
      {gameState === 'playing' && (
        <div className="mobile-touch-controls">
          <div 
            className="mobile-shoot-button"
            onTouchStart={(e) => {
              e.preventDefault();
              const game = gameRef.current;
              game.bullets.push({
                x: game.player.x,
                y: game.player.y,
                angle: game.player.angle
              });
            }}
          >
            <span>🔫</span>
            <small>ATIRAR</small>
          </div>
        </div>
      )}
    </div>
  );
};

export default KillerDeveloper;
